import { useMemo } from 'react';

export interface StepConfig {
  id: number;
  title: string;
  icon: React.ReactNode;
  color: {
    active: string;
    completed: string;
    inactive: string;
  };
  textColor: {
    active: string;
    completed: string;
    inactive: string;
  };
}

export interface UseStepIndicatorsProps {
  currentStep: number;
  totalSteps?: number;
  steps?: StepConfig[];
}

export const useStepIndicators = ({
  currentStep,
  totalSteps = 2,
  steps,
}: UseStepIndicatorsProps) => {
  // Configuración por defecto de los pasos (pantalla unificada de productos/piezas + confirmación)
  const defaultSteps: StepConfig[] = useMemo(
    () => [
      {
        id: 1,
        title: 'Productos y Piezas',
        icon: (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="3" y="3" width="18" height="18" rx="2"></rect>
            <path d="M9 9h6v6H9z"></path>
            <path d="M9 1v6M15 1v6M9 15v8M15 15v8M1 9h6M1 15h6M17 9h6M17 15h6"></path>
          </svg>
        ),
        color: {
          active: 'bg-purple-600',
          completed: 'bg-purple-100',
          inactive: 'bg-gray-200',
        },
        textColor: {
          active: 'text-white',
          completed: 'text-purple-800',
          inactive: 'text-gray-500',
        },
      },
      {
        id: 2,
        title: 'Confirmación',
        icon: (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
            <polyline points="22 4 12 14.01 9 11.01"></polyline>
          </svg>
        ),
        color: {
          active: 'bg-emerald-500',
          completed: 'bg-emerald-100',
          inactive: 'bg-gray-200',
        },
        textColor: {
          active: 'text-white',
          completed: 'text-emerald-800',
          inactive: 'text-gray-500',
        },
      },
    ],
    [],
  );

  // Usar pasos personalizados o los por defecto
  const stepConfigs: StepConfig[] = useMemo(() => {
    return steps || defaultSteps.slice(0, totalSteps);
  }, [steps, defaultSteps, totalSteps]);

  // Determinar el estado de cada paso
  const getStepState = useMemo(() => {
    return (stepId: number) => {
      if (stepId === currentStep) return 'active';
      if (stepId < currentStep) return 'completed';
      return 'inactive';
    };
  }, [currentStep]);

  // Obtener las clases CSS para cada paso
  const getStepClasses = useMemo(() => {
    return (step: StepConfig) => {
      const state = getStepState(step.id);
      return {
        circle: `${step.color[state]} ${step.textColor[state]}`,
        text:
          state === 'active'
            ? `font-semibold ${step.textColor.active.replace('text-', 'text-').replace('-800', '-700')}`
            : step.textColor[state] || 'text-gray-700',
      };
    };
  }, [getStepState]);

  // Validar que el paso actual sea válido
  const isValidStep = useMemo(() => {
    return currentStep > 0 && currentStep <= stepConfigs.length;
  }, [currentStep, stepConfigs.length]);

  return {
    steps: stepConfigs,
    getStepState,
    getStepClasses,
    isValidStep,
    currentStep,
    totalSteps: stepConfigs.length,
  };
};
